import React from 'react';
import {
  Box,
  Button,
  Text,
  Title,
  ActionIcon,
  Modal,
  Tabs,
  Table,
  Paper,
  Badge,
  Divider,
  Grid,
  Card,
  Progress,
  TextInput,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faCircleExclamation,
  faCircleXmark,
  faChartLine,
  faTriangleExclamation,
  faClock,
  faCopy,
  faArrowsRotate,
  faArrowUpRightFromSquare,
  faMagnifyingGlass,
  faReply,
  faCircle,
} from '@fortawesome/pro-light-svg-icons';
import type { RequestTrackingData } from '../../../api/cmail.api';
import {
  trackingItemAmount,
  trackingItemOutcome,
  trackingItemStage,
  trackingItemTitle,
  trackingItemVendor,
} from '../utils/trackingItemLabels';
import type { RequestTrackingStatus } from '../utils/requestTrackingUtils';
import Moment from 'moment';
import classes from './CmailStatusModal.module.css';

const JIRA_BASE_URL = 'https://cowbell.atlassian.net/browse';

interface CmailStatusModalProps {
  open: boolean;
  onClose: () => void;
  missingFields: Record<string, unknown>;
  missingFieldsCount: number;
  missingFieldsDescription: string;
  failureType?: string;
  trackingData?: RequestTrackingData | null;
  warnings: string[];
  jiraTicketNumber?: string;
  onRequestMissingInfo: () => void;
  onProvideMissingInfo: () => void;
  onRefreshTracking?: () => void;
  isRefreshingTracking?: boolean;
  onCreateSalesforceCase?: () => void;
  category?: string | string[];
}

const getStatusCssColor = (status: RequestTrackingStatus): string => {
  switch (status) {
    case 'COMPLETED':
      return 'var(--mantine-color-green-6)';
    case 'FAILED':
      return 'var(--mantine-color-red-6)';
    case 'ESCALATED':
      return 'var(--mantine-color-yellow-6)';
    case 'PENDING':
    case 'IN_PROGRESS':
    default:
      return 'var(--mantine-color-blue-6)';
  }
};

const getStatusMantineColor = (status: RequestTrackingStatus): string => {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'ESCALATED':
      return 'yellow';
    case 'PENDING':
    case 'IN_PROGRESS':
    default:
      return 'blue';
  }
};

const highlightText = (text: string, query: string) => {
  if (!query.trim()) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={index} className={classes.highlight}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Not processed';
  return Moment(dateString).format('MMM D, YYYY h:mm A');
};

const formatCategoryName = (category: string) => {
  if (category === category.toUpperCase()) {
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  }

  return category
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getStatusTitle = (category?: string | string[]) => {
  if (!category) {
    return 'Status';
  }

  const firstCategory = Array.isArray(category) ? category[0] : category;
  if (!firstCategory) {
    return 'Status';
  }

  const formattedCategory = formatCategoryName(firstCategory);

  return `${formattedCategory} Status`;
};

export const CmailStatusModal: React.FC<CmailStatusModalProps> = React.memo(
  ({
    open,
    onClose,
    missingFields,
    missingFieldsCount,
    missingFieldsDescription,
    failureType,
    trackingData,
    warnings,
    jiraTicketNumber,
    onRequestMissingInfo,
    onProvideMissingInfo,
    onRefreshTracking,
    isRefreshingTracking = false,
    onCreateSalesforceCase,
    category,
  }) => {
    const [activeTab, setActiveTab] = React.useState<string | null>(null);
    const [notesSearchQuery, setNotesSearchQuery] = React.useState('');

    const tabs = React.useMemo(() => {
      const result: Array<{
        label: string;
        value: string;
        icon: React.ReactElement;
        visible: boolean;
      }> = [
        {
          label: 'Processing Error',
          value: 'processing-error',
          icon: (
            <FontAwesomeIcon icon={faCircleXmark} className={classes.tabIcon} />
          ),
          visible: Boolean(failureType),
        },
        {
          label: `Missing Fields${
            missingFieldsCount > 0 ? ` (${missingFieldsCount})` : ''
          }`,
          value: 'missing-fields',
          icon: (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className={classes.tabIcon}
            />
          ),
          visible: missingFieldsCount > 0,
        },
        {
          label: 'Request Tracking',
          value: 'request-tracking',
          icon: (
            <FontAwesomeIcon icon={faChartLine} className={classes.tabIcon} />
          ),
          visible: Boolean(trackingData && trackingData.msgId),
        },
        {
          label: `Warnings${
            warnings.length > 0 ? ` (${warnings.length})` : ''
          }`,
          value: 'warnings',
          icon: (
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={classes.tabIcon}
            />
          ),
          visible: warnings.length > 0,
        },
      ];
      return result.filter((tab) => tab.visible);
    }, [missingFieldsCount, failureType, trackingData, warnings]);

    React.useEffect(() => {
      if (open && tabs.length > 0) {
        setActiveTab(tabs[0]?.value || null);
      }
    }, [open, tabs.length]);

    const handleTabChange = (value: string | null) => {
      setActiveTab(value);
    };

    const handleNotesSearchChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setNotesSearchQuery(event.target.value);
    };

    const filteredWarnings = warnings.filter((warning) =>
      warning.toLowerCase().includes(notesSearchQuery.toLowerCase())
    );

    const missingFieldsList = React.useMemo(() => {
      return Object.keys(missingFields);
    }, [missingFields]);

    const handleCopyMessageId = async () => {
      try {
        if (trackingData?.msgId) {
          await navigator.clipboard.writeText(trackingData.msgId);
        }
      } catch (err) {
        console.error('Failed to copy message ID:', err);
      }
    };

    const handleRequestMissingInfo = () => {
      onClose();
      onRequestMissingInfo();
    };

    const handleProvideMissingInfo = () => {
      onClose();
      onProvideMissingInfo();
    };

    const progressPercentage =
      trackingData && trackingData.totalItems > 0
        ? ((trackingData.completedItems +
            trackingData.failedItems +
            trackingData.escalatedItems) /
            trackingData.totalItems) *
          100
        : 0;

    const getOverallStatusColor = () => {
      if (trackingData) {
        return getStatusMantineColor(trackingData.requestStatus);
      }
      return 'blue';
    };

    if (tabs.length === 0) {
      return null;
    }

    const currentTab = activeTab || tabs[0]?.value;

    return (
      <Modal
        opened={open}
        onClose={onClose}
        size="90vw"
        centered
        withCloseButton={false}
        zIndex={9999}
        aria-labelledby="submission-status-modal-title"
        styles={{
          inner: { position: 'fixed', inset: 0 },
          content: {
            maxWidth: '75rem',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={classes.headerIcon}
            />
            <Box>
              <Title order={4} fw={600}>
                {getStatusTitle(category)}
              </Title>
              <Text size="sm" c="dimmed">
                Track Processing Error, Missing Fields, Request Tracking and
                Warnings
              </Text>
            </Box>
          </Box>
          <ActionIcon variant="subtle" onClick={onClose} aria-label="close">
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
        </Box>

        {/* Tabs */}
        <Box className={classes.tabsBorder}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="submission status tabs"
          >
            <Tabs.List>
              {tabs.map((tab) => (
                <Tabs.Tab
                  key={tab.value}
                  value={tab.value}
                  leftSection={tab.icon}
                >
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box className={classes.tabContent}>
          {/* Missing Fields Tab */}
          {currentTab === 'missing-fields' && (
            <Box className={classes.missingFieldsContainer}>
              <Box className={classes.fieldListScroll}>
                <Box component="ul" className={classes.fieldList}>
                  {missingFieldsList.map((field, index) => (
                    <Box
                      key={index}
                      component="li"
                      className={classes.fieldItem}
                    >
                      <FontAwesomeIcon
                        icon={faCircle}
                        className={classes.bulletIcon}
                      />
                      <Text size="sm">{field}</Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Processing Error Tab */}
          {currentTab === 'processing-error' && (
            <Box className={classes.errorScrollArea}>
              <Card withBorder className={classes.errorCard}>
                {/* Header Section */}
                <Box className={classes.errorCardSection}>
                  <Box className={classes.errorHeaderRow}>
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      className={classes.errorIcon}
                    />
                    <Box className={classes.errorContentFlex}>
                      <Box className={classes.errorTitleRow}>
                        <Title order={4} fw={600}>
                          Processing Failed
                        </Title>
                        <Badge
                          color="red"
                          size="sm"
                          className={classes.errorBadge}
                        >
                          ERROR
                        </Badge>
                      </Box>
                      <Text
                        size="sm"
                        c="dimmed"
                        className={classes.errorDescription}
                      >
                        This submission encountered an error during processing
                        and requires attention
                      </Text>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Error Details Section */}
                <Box className={classes.errorDetailsSection}>
                  <Text size="sm" fw={600} mb="xs">
                    Error Details
                  </Text>
                  <Paper
                    withBorder
                    p="md"
                    bg="var(--mantine-color-body)"
                    mb="md"
                  >
                    <Text size="sm" className={classes.codeBlock} p={0}>
                      {failureType}
                    </Text>
                  </Paper>

                  {/* Action Section */}
                  <Text size="sm" fw={600} mb="sm">
                    Next Steps
                  </Text>
                  <Box className={classes.nextStepsColumn}>
                    {jiraTicketNumber ? (
                      <Paper withBorder className={classes.ticketPaper}>
                        <Box className={classes.paperRow}>
                          <Box>
                            <Text size="sm" fw={600} mb={4}>
                              Ticket Created
                            </Text>
                            <Text size="xs" c="dimmed">
                              This issue has been logged and is being tracked
                            </Text>
                          </Box>
                          <Button
                            variant="filled"
                            component="a"
                            rightSection={
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                              />
                            }
                            href={`${JIRA_BASE_URL}/${jiraTicketNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View {jiraTicketNumber}
                          </Button>
                        </Box>
                      </Paper>
                    ) : (
                      <Paper withBorder className={classes.supportPaper}>
                        <Box className={classes.paperRow}>
                          <Box className={classes.paperTextSection}>
                            <Text size="sm" fw={600} mb={4}>
                              Create Support Case
                            </Text>
                            <Text size="xs" c="dimmed">
                              Report this error to track resolution and get
                              updates
                            </Text>
                          </Box>
                          <Button
                            variant="filled"
                            onClick={onCreateSalesforceCase}
                            className={classes.createCaseButton}
                          >
                            Create Case
                          </Button>
                        </Box>
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Card>
            </Box>
          )}

          {/* Request Tracking Tab */}
          {currentTab === 'request-tracking' && trackingData && (
            <Box>
              {/* Header with Refresh */}
              <Box className={classes.trackingHeader}>
                <Box>
                  <Text size="sm" c="dimmed">
                    Message ID: {trackingData.msgId}
                  </Text>
                </Box>
                <Box className={classes.trackingActions}>
                  <ActionIcon
                    variant="subtle"
                    onClick={handleCopyMessageId}
                    size="sm"
                    color="gray"
                  >
                    <FontAwesomeIcon
                      icon={faCopy}
                      className={classes.copyIcon}
                    />
                  </ActionIcon>
                  {onRefreshTracking && (
                    <ActionIcon
                      variant="subtle"
                      onClick={onRefreshTracking}
                      disabled={isRefreshingTracking}
                      size="sm"
                      color="gray"
                    >
                      <FontAwesomeIcon
                        icon={faArrowsRotate}
                        className={`${classes.refreshIcon}${
                          isRefreshingTracking ? ` ${classes.spinIcon}` : ''
                        }`}
                      />
                    </ActionIcon>
                  )}
                </Box>
              </Box>

              {/* Summary Cards */}
              <Grid gutter="sm" mb="md">
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Card withBorder>
                    <Box className={classes.summaryCardContent}>
                      <Title order={3} c="blue" fw={600}>
                        {trackingData.totalItems}
                      </Title>
                      <Text size="sm" c="dimmed">
                        Total Items
                      </Text>
                    </Box>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Card withBorder>
                    <Box className={classes.summaryCardContent}>
                      <Title order={3} c="green" fw={600}>
                        {trackingData.completedItems}
                      </Title>
                      <Text size="sm" c="dimmed">
                        Completed
                      </Text>
                    </Box>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Card withBorder>
                    <Box className={classes.summaryCardContent}>
                      <Title order={3} c="red" fw={600}>
                        {trackingData.failedItems}
                      </Title>
                      <Text size="sm" c="dimmed">
                        Failed
                      </Text>
                    </Box>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Card withBorder>
                    <Box className={classes.summaryCardContent}>
                      <Title order={3} c="yellow" fw={600}>
                        {trackingData.escalatedItems}
                      </Title>
                      <Text size="sm" c="dimmed">
                        Escalated
                      </Text>
                    </Box>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Progress Bar */}
              <Box className={classes.progressSection}>
                <Box className={classes.progressHeader}>
                  <Text size="sm" fw={500}>
                    Overall Progress
                  </Text>
                  <Text size="sm" c="dimmed">
                    {Math.round(progressPercentage)}% Complete
                  </Text>
                </Box>
                <Progress
                  value={progressPercentage}
                  color={getOverallStatusColor()}
                  size="sm"
                  radius="xl"
                />
              </Box>

              {/* Request Info */}
              <Box className={classes.requestInfoSection}>
                <Title order={4} fw={600} className={classes.requestInfoTitle}>
                  Request Information
                </Title>
                <Grid gutter="sm">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Box className={classes.infoRow}>
                      <FontAwesomeIcon
                        icon={faClock}
                        className={classes.infoIcon}
                      />
                      <Text size="sm" fw={500}>
                        Request Started:
                      </Text>
                    </Box>
                    <Text size="sm" c="dimmed" ml="xl">
                      {formatDate(trackingData.requestTimestamp)}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Box className={classes.infoRow}>
                      <FontAwesomeIcon
                        icon={faChartLine}
                        className={classes.infoIcon}
                      />
                      <Text size="sm" fw={500}>
                        Last Updated:
                      </Text>
                    </Box>
                    <Text size="sm" c="dimmed" ml="xl">
                      {formatDate(trackingData.lastUpdateTimestamp)}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Box>

              <Divider my="sm" />

              {/* Items Table */}
              <Box>
                <Title order={4} fw={600} className={classes.detailsTitle}>
                  Details ({trackingData.itemTrackings?.length || 0} items)
                </Title>
                <Paper withBorder>
                  <Table stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th className={classes.headerCell}>
                          Item ID
                        </Table.Th>
                        <Table.Th className={classes.headerCell}>Type</Table.Th>
                        <Table.Th className={classes.headerCell}>
                          Status
                        </Table.Th>
                        <Table.Th className={classes.headerCell}>Info</Table.Th>
                        <Table.Th className={classes.headerCell}>
                          Processed At
                        </Table.Th>
                        <Table.Th className={classes.headerCell}>
                          Notes
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {trackingData.itemTrackings &&
                      Array.isArray(trackingData.itemTrackings) &&
                      trackingData.itemTrackings.length > 0 ? (
                        trackingData.itemTrackings
                          .filter((item) => item)
                          .map((item, index) => (
                            <Table.Tr
                              key={index}
                              className={classes.tableRowHover}
                            >
                              <Table.Td>
                                {/* The correlation id stays reachable on hover for debugging. */}
                                <Text
                                  size="sm"
                                  fw={500}
                                  title={item.itemId ?? undefined}
                                >
                                  {trackingItemTitle(item)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge variant="outline" size="sm">
                                  {item.itemType}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  size="sm"
                                  className={classes.statusBadge}
                                  style={
                                    {
                                      '--status-color': getStatusCssColor(
                                        item.status
                                      ),
                                    } as React.CSSProperties
                                  }
                                >
                                  {item.status}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Box>
                                  {trackingItemAmount(item) && (
                                    <Text
                                      size="sm"
                                      fw={500}
                                      className={classes.itemMetaSmall}
                                    >
                                      {trackingItemAmount(item)}
                                    </Text>
                                  )}
                                  {trackingItemVendor(item) && (
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      className={classes.itemMetaXSmall}
                                    >
                                      {trackingItemVendor(item)}
                                    </Text>
                                  )}
                                  {trackingItemOutcome(item) && (
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      className={classes.itemMetaXSmall}
                                    >
                                      {trackingItemOutcome(item)}
                                    </Text>
                                  )}
                                  {item.metadata?.accountName && (
                                    <Text
                                      size="sm"
                                      fw={500}
                                      className={classes.itemMetaSmall}
                                    >
                                      {item.metadata.accountName}
                                    </Text>
                                  )}
                                  {(item.metadata?.effectiveDate ||
                                    item.metadata?.expirationDate) && (
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      className={classes.itemMetaXSmall}
                                    >
                                      {item.metadata?.effectiveDate && (
                                        <>
                                          Effective:{' '}
                                          {Moment(
                                            item.metadata.effectiveDate
                                          ).format('MMM D, YYYY')}
                                        </>
                                      )}
                                      {item.metadata?.effectiveDate &&
                                        item.metadata?.expirationDate &&
                                        ' • '}
                                      {item.metadata?.expirationDate && (
                                        <>
                                          Expires:{' '}
                                          {Moment(
                                            item.metadata.expirationDate
                                          ).format('MMM D, YYYY')}
                                        </>
                                      )}
                                    </Text>
                                  )}
                                  {!trackingItemAmount(item) &&
                                    !trackingItemVendor(item) &&
                                    !trackingItemOutcome(item) &&
                                    !item.metadata?.accountName &&
                                    !item.metadata?.effectiveDate &&
                                    !item.metadata?.expirationDate && (
                                      <Text
                                        size="sm"
                                        c="dimmed"
                                        className={classes.itemMetaSmall}
                                      >
                                        No additional info
                                      </Text>
                                    )}
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {formatDate(item.processedAt)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {item.failureReason && (
                                  <Box>
                                    {trackingItemStage(item) && (
                                      <Text size="xs" fw={600}>
                                        {item.status === 'ESCALATED'
                                          ? 'Needs review: '
                                          : 'Failed: '}
                                        {trackingItemStage(item)}
                                      </Text>
                                    )}
                                    <Text
                                      size="sm"
                                      c={
                                        item.status === 'ESCALATED'
                                          ? undefined
                                          : 'red'
                                      }
                                    >
                                      {item.failureReason}
                                    </Text>
                                  </Box>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))
                      ) : (
                        <Table.Tr>
                          <Table.Td colSpan={6} className={classes.emptyCell}>
                            <Text size="sm" c="dimmed">
                              No items found
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Warnings/Notes Tab */}
          {currentTab === 'warnings' && (
            <Paper mb="md" className={classes.warningsPaper}>
              <Box className={classes.searchBarWrapper}>
                <Paper
                  component="form"
                  className={classes.searchBar}
                  onSubmit={(event: React.FormEvent) => event.preventDefault()}
                >
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className={classes.searchIcon}
                  />
                  <TextInput
                    variant="unstyled"
                    className={classes.notesSearchInput}
                    placeholder="Search notes..."
                    value={notesSearchQuery}
                    onChange={handleNotesSearchChange}
                  />
                  {notesSearchQuery && (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => setNotesSearchQuery('')}
                      className={classes.clearSearchButton}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </ActionIcon>
                  )}
                </Paper>
              </Box>
              <Box className={classes.warningsScroll}>
                {filteredWarnings.length === 0 ? (
                  <Box className={classes.emptyWarnings}>
                    <Text size="sm">
                      {notesSearchQuery
                        ? 'No matching notes found'
                        : 'No notes available'}
                    </Text>
                  </Box>
                ) : (
                  filteredWarnings.map((warning, index) => {
                    const fieldMatch = warning.match(/Field: ([^,]+),/);
                    const fieldName = fieldMatch
                      ? fieldMatch[1]
                      : 'Unknown field';
                    const valueMatch = warning.match(/EmailValue: ([^,]+),/);
                    const emailValue = valueMatch ? valueMatch[1] : 'N/A';

                    return (
                      <Box key={index} className={classes.warningItem}>
                        <Text size="sm" fw={700} mb={4}>
                          {highlightText(fieldName, notesSearchQuery)}
                        </Text>
                        <Text size="sm" c="dimmed" mb={4}>
                          Value: {highlightText(emailValue, notesSearchQuery)}
                        </Text>
                        <Text
                          size="sm"
                          c="dimmed"
                          className={classes.warningFullText}
                        >
                          {highlightText(warning, notesSearchQuery)}
                        </Text>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Paper>
          )}
        </Box>

        {/* Footer - Only show for Missing Fields tab */}
        {currentTab === 'missing-fields' && (
          <Box className={classes.footer}>
            <Button
              variant="filled"
              onClick={handleRequestMissingInfo}
              className={classes.footerButton}
            >
              <Box className={classes.footerButtonContent}>
                <Box className={classes.footerButtonIconGroup}>
                  <FontAwesomeIcon icon={faReply} size="sm" />
                  <Text size="xs" className={classes.footerButtonLabel}>
                    Reply
                  </Text>
                </Box>
                <Text>Request Missing Information</Text>
              </Box>
            </Button>
            <Button
              variant="filled"
              onClick={handleProvideMissingInfo}
              className={classes.footerButton}
            >
              <Box className={classes.footerButtonContent}>
                <Box className={classes.footerButtonIconGroup}>
                  <FontAwesomeIcon
                    icon={faReply}
                    size="sm"
                    className={classes.forwardIcon}
                  />
                  <Text size="xs" className={classes.footerButtonLabel}>
                    Forward
                  </Text>
                </Box>
                <Text>Provide Missing Information</Text>
              </Box>
            </Button>
          </Box>
        )}
      </Modal>
    );
  }
);
